import { useEffect, useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  MenuItem, Select, FormControl, InputLabel, Switch, FormControlLabel,
  TextField, Tooltip, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import API from "../api";
import { SWAP_MODES, DATA_TYPES, NUMERIC_TYPES, resolveProtocolConfig, validateAddressFromConfig } from "../config/otProtocols";

// Quick-edit for the OT Monitor signal currently selected in OtDeviceSignalSelector —
// every field the full OT Tag Configuration page (Security > OT Configuration) lets
// you change for one tag, without having to find that tag again in the device's full
// table by IP + protocol + address. Same fields, same protocol-driven show/hide rules
// (area/offset/swap only where the protocol supports them) — this is the same tag
// editor, scoped to one row instead of a whole device.
//
// `signal` is the object OtDeviceSignalSelector already resolves from its own signal
// search (full_address, name, protocol, dest_ip, area, address, data_type) — this
// dialog re-fetches the owning device's full config to find that exact tag (matched on
// address + area, the same identity OT Tag Configuration itself keys tags on) so it can
// PUT back the complete tags[] array with just that one entry changed.
const SignalConfigDialog = ({ open, onClose, device, signal, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [allTags, setAllTags] = useState(null);
  const [tagIndex, setTagIndex] = useState(null);
  const [protoConfig, setProtoConfig] = useState(null);
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (!open || !device || !signal?.protocol) return;
    setLoading(true);
    setError(null);
    setAllTags(null);
    setTagIndex(null);
    setForm(null);

    Promise.all([
      API.getOtConfig(device, signal.protocol),
      API.getSystemConfig("ot", "supported_protocols"),
    ])
      .then(([configRes, protoRes]) => {
        const tags = configRes.data?.data?.tags ?? [];
        const idx = tags.findIndex(
          (t) => t.address === signal.address && (t.area ?? "") === (signal.area ?? "")
        );
        setAllTags(tags);
        setTagIndex(idx);

        const protoList = (protoRes.data?.[0]?.value ?? []).map(resolveProtocolConfig);
        const proto = protoList.find((p) => p.key === signal.protocol) ?? null;
        setProtoConfig(proto);

        const t = idx >= 0 ? tags[idx] : {};
        setForm({
          enabled: t.enabled !== false,
          area: t.area ?? (proto?.areas ?? [])[0] ?? "",
          address: t.address ?? signal.address ?? "",
          offset: t.offset ?? "",
          name: t.name ?? signal.name ?? "",
          data_type: t.data_type ?? signal.data_type ?? "int16",
          swap_mode: t.swap_mode ?? "CDAB",
          unit: t.unit ?? "",
          min_value: t.min_value ?? "",
          max_value: t.max_value ?? "",
        });
      })
      .catch(() => setError("Failed to load this signal's configuration."))
      .finally(() => setLoading(false));
  }, [open, device, signal]);

  const isNumeric = form ? NUMERIC_TYPES.has(form.data_type) : false;
  const addrError = form ? validateAddressFromConfig(protoConfig, form.area, form.address) : null;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSave = async () => {
    if (tagIndex == null || tagIndex < 0 || !allTags || !form) return;
    if (addrError) { setError(`Invalid address: ${addrError}`); return; }
    setSaving(true);
    setError(null);
    try {
      const nextTags = allTags.slice();
      nextTags[tagIndex] = {
        ...nextTags[tagIndex],
        enabled: form.enabled,
        area: form.area,
        address: form.address,
        offset: form.offset,
        name: form.name,
        data_type: form.data_type,
        swap_mode: form.swap_mode,
        unit: form.unit,
        min_value: form.min_value === "" ? "" : Number(form.min_value),
        max_value: form.max_value === "" ? "" : Number(form.max_value),
      };
      await API.updateOtConfig(device, signal.protocol, { tags: nextTags });
      onSaved?.();
      onClose();
    } catch {
      setError("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const showAreas = (protoConfig?.areas?.length ?? 0) > 0;
  const showOffset = protoConfig?.supportsOffset ?? false;
  const showSwap = protoConfig?.supportsSwap ?? false;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: "#1a1a1a", border: "1px solid #333", backgroundImage: "none" } }}
    >
      <DialogTitle sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        bgcolor: "#1e1e1e", borderBottom: "1px solid #2e2e2e",
      }}>
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: 15 }}>Signal Configuration</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: "#2e2e2e", bgcolor: "#1a1a1a", pt: 2 }}>
        <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 2, fontFamily: "monospace" }}>
          {device} · {signal?.address ?? "—"}
        </Typography>

        {loading ? (
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Loading…</Typography>
        ) : tagIndex == null || tagIndex < 0 ? (
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            This signal isn't in the device's tag configuration — it may have been auto-discovered but never saved there.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControlLabel
              control={<Switch size="small" checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} color="success" />}
              label={<Typography sx={{ fontSize: 12.5 }}>{form.enabled ? "Enabled — visible in OT Monitor" : "Disabled — hidden from OT Monitor"}</Typography>}
            />

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              {showAreas && (
                <FormControl size="small" sx={{ minWidth: 160, flex: 1 }}>
                  <InputLabel>Area</InputLabel>
                  <Select label="Area" value={form.area} onChange={set("area")}>
                    {protoConfig.areas.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              <Tooltip title={addrError ?? ""} open={!!addrError} placement="top" arrow>
                <TextField
                  size="small" label="Address" fullWidth
                  value={form.address}
                  onChange={set("address")}
                  error={!!addrError}
                  sx={{ flex: 1, minWidth: 140 }}
                />
              </Tooltip>
              {showOffset && (
                <TextField size="small" label="Offset" value={form.offset} onChange={set("offset")} sx={{ width: 90 }} />
              )}
            </Box>

            <TextField size="small" label="Name" fullWidth value={form.name} onChange={set("name")} />

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
              <FormControl size="small" sx={{ minWidth: 140, flex: 1 }}>
                <InputLabel>Data type</InputLabel>
                <Select
                  label="Data type"
                  value={form.data_type}
                  onChange={(e) => setForm((f) => ({ ...f, data_type: e.target.value }))}
                >
                  {DATA_TYPES.map((dt) => <MenuItem key={dt} value={dt}>{dt}</MenuItem>)}
                </Select>
              </FormControl>
              {showSwap && (
                <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
                  <InputLabel>Byte order</InputLabel>
                  <Select label="Byte order" value={form.swap_mode} onChange={set("swap_mode")}>
                    {SWAP_MODES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              <TextField size="small" label="Unit" value={form.unit} onChange={set("unit")} sx={{ width: 90 }} />
            </Box>

            {isNumeric && (
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <TextField size="small" type="number" label="Min value" fullWidth value={form.min_value} onChange={set("min_value")} />
                <TextField size="small" type="number" label="Max value" fullWidth value={form.max_value} onChange={set("max_value")} />
              </Box>
            )}
          </Box>
        )}

        {error && (
          <Typography color="error" variant="caption" sx={{ mt: 1.5, display: "block" }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ bgcolor: "#1e1e1e", borderTop: "1px solid #2e2e2e" }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "text.secondary" }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || tagIndex == null || tagIndex < 0}
          sx={{ textTransform: "none", bgcolor: "#459095", "&:hover": { bgcolor: "#3a7d80" } }}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SignalConfigDialog;
